import type { SignupAnswer, SignupQuestionConfig } from '@workspace/api-backend'
import { useCallback, useEffect, useRef, useState } from 'react'

/** How the app loads and saves the sign-up question; studio passes its API calls in. */
export interface SignupQuestionSource {
  load: () => Promise<SignupQuestionConfig>
  record: (answer: SignupAnswer) => Promise<void>
}

/** The question as worded for this person. */
export function questionTitle(config: SignupQuestionConfig, audience: SignupAnswer['audience']) {
  return audience === 'investor' ? config.investorTitle : config.founderTitle
}

/** Give up on the question rather than hold the screen if the backend is slow. */
const LOAD_TIMEOUT_MS = 5000

/**
 * Loads the sign-up question once `active` turns true. `config` is undefined
 * while loading and null when there is nothing to ask: no source (e.g. an
 * invite), switched off in Admin, or the load failed.
 */
export function useSignupQuestion(source: SignupQuestionSource | undefined, active: boolean) {
  const [config, setConfig] = useState<SignupQuestionConfig | null | undefined>(undefined)
  const answer = useRef<SignupAnswer | null>(null)
  const saved = useRef(false)

  useEffect(() => {
    if (!source || !active || config !== undefined) return
    let cancelled = false
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), LOAD_TIMEOUT_MS))
    Promise.race([source.load(), timeout])
      .then((loaded) => !cancelled && setConfig(loaded?.enabled ? loaded : null))
      .catch(() => !cancelled && setConfig(null))
    return () => {
      cancelled = true
    }
  }, [source, active, config])

  // The card remounts blank when processing is re-entered (e.g. after an error
  // sends the person back to the form), so a pick from the last visit must not linger.
  useEffect(() => {
    if (!active) answer.current = null
  }, [active])

  const current = source ? config : null

  /** Saves the latest answer, or a skip, once. Never throws: a failed save must not stop onboarding. */
  const save = useCallback(
    (audience: SignupAnswer['audience']) => {
      if (!source || !current || saved.current) return
      saved.current = true
      // Continue with nothing picked counts as a skip of the question shown.
      source
        .record(answer.current ?? { audience, question: questionTitle(current, audience) })
        .catch(() => {})
    },
    [source, current]
  )

  return { config: current, answer, save }
}
